/**
 * Who she is today, in one place, for everything that needs to know.
 *
 * Before this existed each surface worked it out for itself, and the results
 * disagreed. The greeting computed a mood that could only ever be `neglected`
 * or nothing, so ten of the seventeen lines written for loyal mode could not
 * be reached from the app at all. The ring commentary passed no mood, so the
 * over-budget lines written for a bad week never fired during one. The empty
 * day passed neither, which stranded the entire neglect register written for
 * exactly that surface — twenty-four lines about coming back after a silence,
 * on the one screen that only ever shows during a silence.
 *
 * None of that was a bug in the catalogue. It was three call sites each
 * knowing a different amount. So: one query, one answer, everybody reads it.
 *
 * The store also stamps `data-her-tone` and `data-big-day` on the document
 * element, which is how a mood computed in TypeScript reaches a border colour
 * and a breathing rate that only CSS can see.
 */

import {
  type BigDayKey,
  bigDayFor,
  computeMood,
  isKateBirthday,
  type MoodKey,
  type PresenceTone,
  presenceTone,
} from '@gramas/shared'
import { create } from 'zustand'
import { getMeta, setMeta } from '../db/dexie.js'
import { herHour } from '../glitime/clock.js'

export interface Presence {
  /** The diary's own state, in one of six words. */
  mood: MoodKey
  /** The mood plus Wednesday: what she looks like rather than what she is. */
  tone: PresenceTone
  /** 0 when today has an entry, otherwise how long the silence has run. */
  daysSinceLastLog: number
  /** Her own day, if the seed granted her one — an event id or a saga beat. */
  herLife: string | null
  /** True when today's her-life beat ends a saga rather than continuing one. */
  sagaFinale: string | null
  /** One of the five days the instrument itself changes colour, or null. */
  bigDay: BigDayKey | null
  /** The date this was computed for, so a rollover can be noticed. */
  date: string
  /** False until the first real query lands; the defaults below are a guess. */
  ready: boolean
}

/**
 * Plain until told otherwise. Every field here is what an app that has just
 * launched and read nothing should assume, and `ready` is how a caller tells
 * that apart from a genuinely plain day.
 */
export const usePresence = create<Presence>(() => ({
  mood: 'plain',
  tone: 'plain',
  daysSinceLastLog: 0,
  herLife: null,
  sagaFinale: null,
  bigDay: null,
  date: '',
  ready: false,
}))

/** Read once, outside React — for the toast layer and the sound engine. */
export function currentPresence(): Presence {
  return usePresence.getState()
}

export interface PresenceUpdate {
  mood: MoodKey
  daysSinceLastLog: number
  herLife: string | null
  sagaFinale: string | null
  bigDay: BigDayKey | null
  date: string
  now: Date
}

export function setPresence(update: PresenceUpdate): void {
  const tone = presenceTone(update.now, update.mood)
  usePresence.setState({
    mood: update.mood,
    tone,
    daysSinceLastLog: update.daysSinceLastLog,
    herLife: update.herLife,
    sagaFinale: update.sagaFinale,
    bigDay: update.bigDay,
    date: update.date,
    ready: true,
  })
  stamp(tone, update.bigDay, isKateBirthday(update.now), herHour(update.now))
}

/**
 * The whole of the TypeScript-to-CSS bridge. Three attributes on the root
 * element: one selects her palette and her breathing, one selects a
 * whole-app skin on the five days that get one, and one — a single day a
 * year — puts raccoon banding on her rings. That last one is client-only
 * cosmetics with no push counterpart, which is fine: a lock screen has no
 * rings to stripe.
 */
function stamp(tone: PresenceTone, bigDay: BigDayKey | null, kateDay: boolean, hour: number): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.dataset.herTone = tone
  if (bigDay == null) {
    delete root.dataset.bigDay
  } else {
    root.dataset.bigDay = bigDay
  }
  if (kateDay) {
    root.dataset.kateDay = '1'
  } else {
    delete root.dataset.kateDay
  }
  // Small hours: her zone dims, because she has plainly been woken up. The
  // controller re-stamps every minute, so this arrives and leaves on its own.
  if (hour < NIGHT_UNTIL_HOUR) {
    root.dataset.herNight = '1'
  } else {
    delete root.dataset.herNight
  }
}

/** Before this hour she is up at an hour she did not choose. */
const NIGHT_UNTIL_HOUR = 5

/** What the mood is once the diary has been read; see `PresenceController`. */
export function moodFrom(
  basis: Omit<Parameters<typeof computeMood>[0], 'daysSinceLastLog'>,
  daysSinceLastLog: number,
): MoodKey {
  return computeMood({ ...basis, daysSinceLastLog })
}

export { bigDayFor }

function bigDayCueKey(owner: string): string {
  return `glitime.bigdayCue.${owner}`
}

/**
 * Whether the big-day chirp should play right now: once per local day per
 * account, whichever big day it turns out to be. `PresenceController`
 * recomputes `bigDay` on every live-query rerun — a log written, a tab
 * refocused — which is far more often than once a day, so the guard has to
 * outlive the render it fires from. The caller has already checked that
 * today is a big day at all; this only answers "has today's chirp gone out
 * yet". Kept here rather than beside `playCue` itself so this module never
 * has to import the sound engine, which already imports `currentPresence`
 * from here.
 */
// Two rapid calls for the same owner (a live-query rerun firing before the
// previous check's write has landed) must not both read "not yet taken" —
// chaining each call off the previous one serializes the read-then-write
// per owner without needing a persistent in-memory cache.
const pending = new Map<string, Promise<unknown>>()

export async function takeBigDayCue(date: string, owner: string): Promise<boolean> {
  const key = bigDayCueKey(owner)
  const prior = pending.get(owner) ?? Promise.resolve()
  const run = prior.then(async () => {
    const last = await getMeta<string | null>(key, null)
    if (last === date) return false
    await setMeta(key, date)
    return true
  })
  pending.set(
    owner,
    run.catch(() => undefined),
  )
  return run
}
